/* Outline
 * app.js -> this file.
 * |
 * | - /barba/
 * |    barbajs管理用のディレクトリ
 * |    barbajs : https://barba.js.org/
 * |    シームレスな画面遷移を実現するためのPJAXライブラリ。
 * |
 * | - /route/
 * |    ページ管理用のディレクトリ
 * |    ページ単体での動作等が必要な場合に用いる。
 * |    作成した関数は、barba/exe.jsのviewで管理、適応する。
 * |
 * | - /modules/
 * |    各モジュールの管理用ディレクトリ
 * |    モジュールではないが、大きめの挙動を管理する場合、活用OK。
 * */

/* ********************************************
 * import
 * ****************************************** */
import {config} from './config';
import {isMobile, mediaQuery, setViewport, supportsWebp} from "./utils/utils";

// gsap関連
import {gsap} from 'gsap';
import {ScrollToPlugin} from "gsap/ScrollToPlugin";
import {ScrollSmoother} from "gsap/ScrollSmoother";
import {CustomEase} from "gsap/CustomEase";
import {ScrollTrigger} from "gsap/ScrollTrigger";
import {SplitText} from "gsap/SplitText";
gsap.registerPlugin(ScrollSmoother, CustomEase, ScrollTrigger, SplitText, ScrollToPlugin);
CustomEase.create("fade", "0.5, 1, 0.89, 1");
CustomEase.create("fadeCurtain", ".25, .46, .45, .94");
CustomEase.create("slide", ".32, .95, .6, 1");
CustomEase.create("curtain", ".25, .43, .78, .71");


import {initBarba} from './barba/barba';
import {initScrollSmoother} from './modules/scrollSmoother';
import {menu} from './ui/menu';
import {gnav} from './ui/gnav';
import {scrollResetPosition} from './modules/scrollResetPosition';
import {scrollContentsObserver, scrollToTop, scrollUpDownObserver, imageParallaxScrollEffect} from './ui/scroll';


/* =============================================================
 * variables
 * ========================================================== */
const body = document.body;
// const currentWindowWidth = window.innerWidth;
// const currentDevice = mediaQuery();

/* ********************************************
 * ページ遷移時に毎回実行する関数
 * ****************************************** */
export const appInit = () => {
  // Utility関連の関数
  supportsWebp(); // webp非サポートデバイスで特定のクラス付与
  setViewport(); // スマホデバイスのvh, scrollbarを含まないvwを正確に取得

  // UI関連の関数
  gnav(); // グローバルナビの挙動
  scrollToTop(); // ScrollTopに戻るリンク

  // アニメーション関連の関数
  scrollUpDownObserver(); // 上下スクロールの検知
  scrollContentsObserver(); // ViewPortに入ったコンテンツに特定のクラスを付与

  if (!isMobile() && mediaQuery() === 'pc') {
    imageParallaxScrollEffect(); // パララックス画像
  }


};

/* ********************************************
 * landing
 * DOMを読み込んだ後
 * ****************************************** */
document.addEventListener('DOMContentLoaded', ()=> {

  // 状態監視用のクラス追加
  body.classList.add('content_loading');
  if (isMobile()) {
    body.classList.add('touch_device'); // touch device時に各種アニメーション等々を消すためのクラス（主にlanding時）
  }

  // SPAの実行
  if (config.enableBarba) {
    initBarba();
  }
  // 慣性スクロールの実行
  if (config.enableScrollSmoother) {
    initScrollSmoother();
  }

  // 初回ランディング時のみ実行
  menu(); // メニュー（旧ハンバーガーメニュー）の挙動

  // ページ遷移の度に実行
  appInit();

  /*
   * barba.js/afterEnterに入れる場合、ランディング時にも発火するため、重複してしまうので注意。
   * 重複を避ける場合、afterに入れるか、appInit内にのみ入れること。
   */
});


/* ********************************************
 * loaded
 * リソース全て読み込んだ後
 * ****************************************** */
window.addEventListener('load', () => {
  body.classList.remove('content_loading');
  body.classList.add('content_loaded');
  scrollResetPosition(0); // ランディング時のhash付きURLの処理
});


/* ********************************************
 * hashchanged
 * URLハッシュの変更が発生した場合
 * example.com/ → example.com/#hash のような形で変更が発生した場合
 * ****************************************** */
window.addEventListener('hashchange', () => {
  console.log('hashchange');
  // 途中でハッシュが変わる際の挙動が少し特殊で、ページリロード時同様デフォルトのスクロールと干渉するため、慣性スクロールの初期化が必須
  if (config.enableScrollSmoother) {
    initScrollSmoother();

    // ScrollSmoother初期化後にハッシュ位置へスクロールさせる
    if (window.location.hash) {
      const smoother = ScrollSmoother.get();
      const to = document.querySelector(window.location.hash);
      if (to) {
        smoother.scrollTop(to.offsetTop);
      }
    }
  }
});



/* ********************************************
 * window.resize
 * 画面サイズに変更が発生したタイミング
 * ****************************************** */
window.addEventListener('resize', function() {
  setViewport();
  // switchMediaReload(currentWindowWidth, currentDevice); // PC→SPなどのデバイスサイズ（デザイン基準）の変更が発生した際にリロード
});
